import React, { useState, useEffect, useMemo } from 'react';
import { 
  Eye, 
  EyeOff, 
  Search, 
  Save, 
  RefreshCw, 
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  CheckCircle2,
  Database,
  Tag,
  Filter,
  X,
  Info,
  Shield,
  Settings
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { getAllEntityMetadata, clearConfigCache } from '../../utils/entityConfig';

const FieldVisibilityManager = () => {
  const { user } = useAuth();
  const tenantCode = user?.tenantCode || null;
  const isSuperAdmin = user?.roles?.some(r => (r.RoleName || r) === 'SuperAdmin');

  // State management
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityConfig, setEntityConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroup, setFilterGroup] = useState('all');
  const [hasChanges, setHasChanges] = useState(false);
  
  // Field visibility state
  const [visibleFields, setVisibleFields] = useState([]);
  const [hiddenFields, setHiddenFields] = useState([]);
  const [originalHiddenFields, setOriginalHiddenFields] = useState([]);

  // Load entity list on mount
  useEffect(() => {
    loadEntities();
  }, []);

  // Load entity config when entity is selected
  useEffect(() => {
    if (selectedEntity) {
      loadEntityConfig(selectedEntity.code);
    }
  }, [selectedEntity]);

  const loadEntities = async () => {
    try {
      const entityMetadata = await getAllEntityMetadata({ forceRefresh: true });
      setEntities(entityMetadata);
      
      // Auto-select first entity
      if (entityMetadata.length > 0) {
        setSelectedEntity(entityMetadata[0]);
      }
    } catch (err) {
      console.error('Error loading entities:', err);
      setError('Failed to load entities');
      toast.error('Failed to load entities');
    }
  };

  const loadEntityConfig = async (entityName) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/tenant-entity-config/${entityName}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load entity configuration');
      }

      const data = await response.json();
      const config = data.config || data;
      
      setEntityConfig(config);
      
      // Extract hidden fields from config
      const hiddenFieldsList = config.hiddenFields || [];
      setOriginalHiddenFields([...hiddenFieldsList]);
      
      // Separate fields into visible and hidden
      const allFields = Object.keys(config.fields || {}).map(fieldName => ({
        name: fieldName,
        ...config.fields[fieldName]
      }));
      
      const visible = allFields.filter(f => !hiddenFieldsList.includes(f.name));
      const hidden = allFields.filter(f => hiddenFieldsList.includes(f.name));
      
      setVisibleFields(visible);
      setHiddenFields(hidden);
      setHasChanges(false);
    } catch (err) {
      console.error('Error loading entity config:', err);
      setError(err.message);
      toast.error('Failed to load entity configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleHideField = (field) => {
    setVisibleFields(prev => prev.filter(f => f.name !== field.name));
    setHiddenFields(prev => [...prev, field]);
    setHasChanges(true);
  };

  const handleShowField = (field) => {
    setHiddenFields(prev => prev.filter(f => f.name !== field.name));
    setVisibleFields(prev => [...prev, field]);
    setHasChanges(true);
  };

  const handleHideMultiple = (fields) => {
    setVisibleFields(prev => prev.filter(f => !fields.some(field => field.name === f.name)));
    setHiddenFields(prev => [...prev, ...fields]);
    setHasChanges(true);
  };

  const handleShowMultiple = (fields) => {
    setHiddenFields(prev => prev.filter(f => !fields.some(field => field.name === f.name)));
    setVisibleFields(prev => [...prev, ...fields]);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!selectedEntity || !hasChanges) return;

    setSaving(true);
    try {
      const newHiddenFields = hiddenFields.map(f => f.name);
      
      // Get existing overrides
      const overridesResponse = await fetch(
        `/api/tenant-entity-config/${selectedEntity.code}/overrides`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Tenant-Code': tenantCode
          }
        }
      );

      let existingOverrides = {};
      if (overridesResponse.ok) {
        const data = await overridesResponse.json();
        existingOverrides = data.overrides || {};
      }

      // Update overrides with new hidden fields
      // IMPORTANT: Remove invalid customFields to prevent validation errors
      const updatedOverrides = {
        ...existingOverrides,
        hiddenFields: newHiddenFields
      };
      
      // Remove customFields with missing database mappings
      if (updatedOverrides.customFields) {
        Object.keys(updatedOverrides.customFields).forEach(fieldName => {
          const field = updatedOverrides.customFields[fieldName];
          if (!field.database) {
            console.warn(`Removing invalid customField ${fieldName} (missing database mapping)`);
            delete updatedOverrides.customFields[fieldName];
          }
        });
        // If no valid customFields remain, remove the property entirely
        if (Object.keys(updatedOverrides.customFields).length === 0) {
          delete updatedOverrides.customFields;
        }
      }

      // Save overrides
      const response = await fetch(
        `/api/tenant-entity-config/${selectedEntity.code}/overrides`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Tenant-Code': tenantCode
          },
          body: JSON.stringify({ overrides: updatedOverrides })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save field visibility settings');
      }

      // Clear cache and dispatch event
      clearConfigCache(selectedEntity.code);
      window.dispatchEvent(new CustomEvent('entity-config-updated', {
        detail: { entityName: selectedEntity.code }
      }));

      setOriginalHiddenFields(newHiddenFields);
      setHasChanges(false);
      toast.success('Field visibility settings saved successfully!');
    } catch (err) {
      console.error('Error saving field visibility:', err);
      toast.error('Failed to save field visibility settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!window.confirm('Are you sure you want to discard all changes?')) {
      return;
    }

    // Restore original state
    const allFields = [...visibleFields, ...hiddenFields];
    const visible = allFields.filter(f => !originalHiddenFields.includes(f.name));
    const hidden = allFields.filter(f => originalHiddenFields.includes(f.name));
    
    setVisibleFields(visible);
    setHiddenFields(hidden);
    setHasChanges(false);
  };

  const handleResetToGlobal = async () => {
    if (!selectedEntity) return;

    if (!window.confirm('Reset to global defaults? This will clear all tenant-specific field visibility overrides.')) {
      return;
    }

    setSaving(true);
    try {
      // Get existing overrides
      const overridesResponse = await fetch(
        `/api/tenant-entity-config/${selectedEntity.code}/overrides`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Tenant-Code': tenantCode
          }
        }
      );

      let existingOverrides = {};
      if (overridesResponse.ok) {
        const data = await overridesResponse.json();
        existingOverrides = data.overrides || {};
      }

      // Clear hidden fields
      const updatedOverrides = {
        ...existingOverrides,
        hiddenFields: []
      };

      // Save overrides
      const response = await fetch(
        `/api/tenant-entity-config/${selectedEntity.code}/overrides`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Tenant-Code': tenantCode
          },
          body: JSON.stringify({ overrides: updatedOverrides })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to reset to global defaults');
      }

      // Reload config
      await loadEntityConfig(selectedEntity.code);

      // Clear cache and dispatch event
      clearConfigCache(selectedEntity.code);
      window.dispatchEvent(new CustomEvent('entity-config-updated', {
        detail: { entityName: selectedEntity.code }
      }));

      toast.success('Reset to global defaults successfully!');
    } catch (err) {
      console.error('Error resetting to global:', err);
      toast.error('Failed to reset to global defaults');
    } finally {
      setSaving(false);
    }
  };

  // Get unique groups from all fields
  const allGroups = useMemo(() => {
    const groups = new Set();
    [...visibleFields, ...hiddenFields].forEach(field => {
      const group = field.group || field.ui?.group || 'General';
      groups.add(group);
    });
    return ['all', ...Array.from(groups).sort()];
  }, [visibleFields, hiddenFields]);

  // Filter fields based on search and group
  const filterFields = (fields) => {
    return fields.filter(field => {
      const matchesSearch = !searchTerm || 
        field.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (field.displayName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (field.displayNameTH || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const group = field.group || field.ui?.group || 'General';
      const matchesGroup = filterGroup === 'all' || group === filterGroup;
      
      return matchesSearch && matchesGroup;
    });
  };

  const filteredVisibleFields = useMemo(() => filterFields(visibleFields), [visibleFields, searchTerm, filterGroup]);
  const filteredHiddenFields = useMemo(() => filterFields(hiddenFields), [hiddenFields, searchTerm, filterGroup]);

  if (!tenantCode && !isSuperAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            No Tenant Context
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            You must be associated with a tenant to manage field visibility.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <EyeOff className="h-7 w-7" />
            <span>Field Visibility Manager</span>
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Control which fields are visible for {tenantCode || 'your tenant'}
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {hasChanges && (
            <button
              onClick={handleReset}
              className="bg-gray-600 dark:bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center space-x-2"
              disabled={saving}
            >
              <X className="h-4 w-4" />
              <span>Discard Changes</span>
            </button>
          )}
          
          <button
            onClick={handleResetToGlobal}
            className="bg-purple-600 dark:bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 flex items-center space-x-2"
            disabled={saving || loading}
          >
            <RefreshCw className="h-4 w-4" />
            <span>Reset to Global</span>
          </button>

          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="bg-blue-600 dark:bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Entity Selector */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Entity
        </label>
        <select
          value={selectedEntity?.code || ''}
          onChange={(e) => {
            if (hasChanges) {
              if (!window.confirm('You have unsaved changes. Discard them?')) {
                return;
              }
            }
            const entity = entities.find(ent => ent.code === e.target.value);
            setSelectedEntity(entity);
          }}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          {entities.map(entity => (
            <option key={entity.code} value={entity.code}>
              {entity.displayName} ({entity.name})
            </option>
          ))}
        </select>
      </div>

      {/* Search and Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search Fields
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by field name or display name..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Filter by Group
            </label>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                value={filterGroup}
                onChange={(e) => setFilterGroup(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {allGroups.map(group => (
                  <option key={group} value={group}>
                    {group === 'all' ? 'All Groups' : group}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Info Banner */}
      {hasChanges && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Unsaved Changes</h3>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
              You have unsaved changes. Click "Save Changes" to apply them.
            </p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Field Lists */}
      {!loading && entityConfig && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Visible Fields */}
          <FieldListPanel
            title="Visible Fields"
            icon={Eye}
            fields={filteredVisibleFields}
            totalCount={visibleFields.length}
            filteredCount={filteredVisibleFields.length}
            onMoveField={handleHideField}
            onMoveAll={() => handleHideMultiple(filteredVisibleFields)}
            moveIcon={ChevronRight}
            moveLabel="Hide"
            emptyMessage="No visible fields"
            color="green"
          />

          {/* Hidden Fields */}
          <FieldListPanel
            title="Hidden Fields"
            icon={EyeOff}
            fields={filteredHiddenFields}
            totalCount={hiddenFields.length}
            filteredCount={filteredHiddenFields.length}
            onMoveField={handleShowField}
            onMoveAll={() => handleShowMultiple(filteredHiddenFields)}
            moveIcon={ChevronLeft}
            moveLabel="Show"
            emptyMessage="No hidden fields"
            color="red"
          />
        </div>
      )}
    </div>
  );
};

// Field List Panel Component
const FieldListPanel = ({
  title,
  icon: Icon,
  fields,
  totalCount,
  filteredCount,
  onMoveField,
  onMoveAll,
  moveIcon: MoveIcon,
  moveLabel,
  emptyMessage,
  color
}) => {
  const colorClasses = {
    green: {
      bg: 'bg-green-100 dark:bg-green-900/30',
      text: 'text-green-700 dark:text-green-300',
      border: 'border-green-200 dark:border-green-700',
      button: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40'
    },
    red: {
      bg: 'bg-red-100 dark:bg-red-900/30',
      text: 'text-red-700 dark:text-red-300',
      border: 'border-red-200 dark:border-red-700',
      button: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40'
    }
  };

  const colors = colorClasses[color] || colorClasses.green;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col" style={{ height: '600px' }}>
      {/* Header */}
      <div className={`${colors.bg} ${colors.border} border-b p-4`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <Icon className={`h-5 w-5 ${colors.text}`} />
            <h3 className={`font-semibold ${colors.text}`}>{title}</h3>
          </div>
          <span className={`text-sm ${colors.text}`}>
            {filteredCount === totalCount ? totalCount : `${filteredCount} / ${totalCount}`} fields
          </span>
        </div>
        
        {fields.length > 0 && (
          <button
            onClick={onMoveAll}
            className={`w-full px-3 py-2 rounded-lg flex items-center justify-center space-x-2 text-sm font-medium ${colors.button}`}
          >
            <MoveIcon className="h-4 w-4" />
            <span>{moveLabel} All Filtered</span>
          </button>
        )}
      </div>

      {/* Field List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {fields.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{emptyMessage}</p>
          </div>
        ) : (
          fields.map(field => (
            <FieldCard
              key={field.name}
              field={field}
              onMove={() => onMoveField(field)}
              moveIcon={MoveIcon}
              moveLabel={moveLabel}
            />
          ))
        )}
      </div>
    </div>
  );
};

// Field Card Component
const FieldCard = ({ field, onMove, moveIcon: MoveIcon, moveLabel }) => {
  const getBadgeColor = () => {
    if (field._tenantSpecific) return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300';
    if (field._tenantModified) return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
    return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  };

  const getBadgeLabel = () => {
    if (field._tenantSpecific) return 'Custom';
    if (field._tenantModified) return 'Modified';
    return 'Global';
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:border-blue-500 dark:hover:border-blue-400 transition-colors">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {field.name}
            </h4>
            <span className={`text-xs px-2 py-0.5 rounded-full ${getBadgeColor()}`}>
              {getBadgeLabel()}
            </span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
            {field.displayName || field.name}
            {field.displayNameTH && field.displayNameTH !== field.displayName && (
              <span className="ml-2 text-gray-500">({field.displayNameTH})</span>
            )}
          </p>
        </div>
        <button
          onClick={onMove}
          className="ml-3 p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex-shrink-0"
          title={moveLabel}
        >
          <MoveIcon className="h-4 w-4" />
        </button>
      </div>
      
      <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center space-x-1">
          <Database className="h-3 w-3" />
          <span className="truncate">{field.database?.table || 'N/A'}.{field.database?.column || 'N/A'}</span>
        </div>
        <div className="flex items-center space-x-1">
          <Tag className="h-3 w-3" />
          <span>{field.dataType || 'text'}</span>
        </div>
        {field.group && (
          <div className="flex items-center space-x-1">
            <Settings className="h-3 w-3" />
            <span className="truncate">{field.group}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldVisibilityManager;

